import { FC, ReactNode, useEffect, useMemo, useState } from "react";

import { Box } from "../../../../../components/box";
import FontFa from "../../../../../components/fontFa/fontFa";

import { Spin } from "antd";
import { DateFormat } from "../../../../../constants";
import { DateHelper } from "../../../../../helper";
import "./approval-process.scss";

type OptionLike = {
  label?: string;
  value?: string;
  Title?: string;
  eMail?: string;
  name?: string;
  email?: string;
};

export interface ApprovalProcessUser {
  name: string;
  email: string;
}

export interface ApprovalProcessStep {
  id?: number;
  title: string;
  users: ApprovalProcessUser[];
  defaultVisible?: number;
}

export interface ApprovalProcessHistory {
  label: string;
  status: string;
  updatedDateTime: string;
  reason: string;
}

export interface ApprovalProcessApprover {
  title: string;
  name: string;
  email: string;
  isRequired: boolean;
  roles: string[];
  isApproved: boolean;
  isRejected: boolean;
  comment?: string;
  histories: ApprovalProcessHistory[];
  isSub?: boolean;
}

export interface ApprovalProcessProps {
  className?: string;
  title?: string | ReactNode;
  requester?: ApprovalProcessUser | null;
  approverOptions?: OptionLike[];
  administrativeOptions?: OptionLike[];
  officerOptions?: OptionLike[];
  executorOptions?: OptionLike[];
  steps?: ApprovalProcessApprover[][];
  initStep?: ApprovalProcessApprover[];
  loading?: boolean;
  activeStep?: number;
  allowClickSteps?: number[];
  onClickStep?: (step: number) => void;
}

export const ApprovalProcess: FC<ApprovalProcessProps> = ({
  className,
  title = "Quy trình duyệt",
  steps,
  initStep,
  loading = false,
  activeStep = 4,
  allowClickSteps,
  onClickStep,
}) => {
  const derivedSteps = useMemo<ApprovalProcessApprover[][]>(() => {
    if (!steps?.length) return [];

    const sortedSteps = steps.map((item) => item.sort((a, b) => (a.isApproved || a.isRejected ? -1 : 1)));

    if (initStep) return [initStep, ...sortedSteps];

    return sortedSteps;
  }, [steps, initStep]);

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  const toggleStep = (stepId: number) => {
    setExpanded((prev) => ({
      ...prev,
      [stepId]: !prev[stepId],
    }));
  };

  const toggleAllSteps = () => {
    if (isAllExpanded) {
      // Collapse all
      setExpanded({});
      setIsAllExpanded(false);
    } else {
      // Expand all
      const allExpanded: Record<number, boolean> = {};
      derivedSteps.forEach((_, index) => {
        if (derivedSteps[index].length > 1) {
          allExpanded[index] = true;
        }
      });
      setExpanded(allExpanded);
      setIsAllExpanded(true);
    }
  };

  // Sync isAllExpanded với expanded state
  useEffect(() => {
    const expandableSteps = derivedSteps.map((step, index) => ({ step, index })).filter(({ step }) => step.length > 1);

    if (expandableSteps.length === 0) {
      setIsAllExpanded(false);
      return;
    }

    const allExpanded = expandableSteps.every(({ index }) => expanded[index] === true);
    setIsAllExpanded(allExpanded);
  }, [expanded, derivedSteps]);

  const handleClickStep = (step: number) => {
    if (!allowClickSteps?.includes(step)) return;

    onClickStep?.(step);
  };

  const hasExpandableSteps = derivedSteps.some((step) => step.length > 1);

  return (
    <Box
      title={title}
      className={className}
      titleRight={
        hasExpandableSteps ? (
          <button
            type="button"
            className="approval-process__expand-all"
            onClick={toggleAllSteps}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px 8px",
              fontSize: "14px",
              color: "#1890ff",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <FontFa name={isAllExpanded ? "faArrowUp" : "faArrowDown"} />
            {isAllExpanded ? "Thu gọn" : "Mở rộng"}
          </button>
        ) : null
      }
    >
      <Spin spinning={loading}>
        <div className="TimelineBox horizontal mdHorizontal mdSmall approval-process">
          {derivedSteps.map((step, index) => {
            const isExpanded = expanded[index];
            const visibleCount = isExpanded ? step.length : 1;
            const visibleUsers = step.slice(0, visibleCount);
            const remaining = step.length - visibleUsers.length;
            const stepTitle = (step.length && step.find((item) => !item.isSub)?.title) || "";
            const isStepApproved = step.some((approver) => approver.isApproved);
            const isStepRejected = step.some((approver) => approver.isRejected);

            const getStepIcon = () => {
              if (isStepRejected) {
                return <FontFa name="faBan" />;
              }
              if (isStepApproved) {
                return <FontFa name="faCheck" />;
              }
              return index + 1;
            };

            const getStepClassName = () => {
              if (isStepRejected) return "rejected";
              if (isStepApproved) return "approvedV2";
              return "waitingV2";
            };

            return (
              <div key={index} className="item">
                <div
                  className={`step ${getStepClassName()} ${activeStep === index + 1 ? "step--active" : ""} ${
                    (allowClickSteps || []).includes(index + 1) ? "step--allow-click" : ""
                  }`}
                  onClick={() => handleClickStep(index + 1)}
                >
                  {getStepIcon()}
                </div>
                <div className="approval-process__title">{stepTitle}</div>
                <div className="content">
                  {visibleUsers.length ? (
                    visibleUsers.map((user, userIndex) => {
                      const historyProcess = step[userIndex]?.histories
                        ?.filter((item) => ["rejected", "approved"].includes(item.status))
                        ?.at(-1);

                      const dateProcess = historyProcess?.updatedDateTime
                        ? DateHelper.formatDateToString(historyProcess.updatedDateTime, {
                            format: DateFormat.DD_MM_YYYY_HH_MM,
                          })
                        : "";
                      const isApproved = historyProcess?.status === "approved";

                      const createdDate = step[userIndex]?.histories.find(
                        (item) => item.status === "created",
                      )?.updatedDateTime;
                      const createdDateFormatted = createdDate
                        ? DateHelper.formatDateToString(createdDate, { format: DateFormat.DD_MM_YYYY_HH_MM })
                        : "";
                      const createdDateText = createdDateFormatted ? `Ngày tạo: ${createdDateFormatted}` : "";

                      return (
                        <div
                          key={`${index}-${user.email}`}
                          className={`approval-process__card ${
                            step[userIndex]?.isApproved
                              ? "approval-process__card--approved"
                              : step[userIndex]?.isRejected
                              ? "approval-process__card--rejected"
                              : ""
                          }`}
                        >
                          <div className="approval-process__title">{step[userIndex]?.roles?.at?.(0)}</div>
                          <div className="approval-process__name">
                            {user.name} {user.isSub && <span className="approval-process__sub">(Ủy quyền)</span>}
                          </div>
                          <div className="approval-process__email">{user.email}</div>
                          {createdDateText && <div className="approval-process__date">{createdDateText}</div>}
                          {dateProcess && (
                            <div className="approval-process__date">
                              {isApproved ? "Ngày duyệt" : "Ngày từ chối"}: {dateProcess}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="approval-process__empty">Chưa có người phê duyệt</div>
                  )}

                  {remaining > 0 && (
                    <button type="button" className="approval-process__toggle" onClick={() => toggleStep(index)}>
                      {isExpanded ? "Thu gọn" : `Xem thêm ${remaining} người`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Spin>
    </Box>
  );
};

export default ApprovalProcess;
